import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSearch } from '../contexts/SearchContext';
import SearchSorting from '../components/ui/SearchSorting';
import RelatedSearches from '../components/ui/RelatedSearches';
import ProductCard from '../components/ui/ProductCard';
import { trackProductSearch } from '../services/api';

const SearchPage = () => {
  const { 
    searchQuery, 
    searchResults, 
    isSearching, 
    performSearch, 
    totalResults, 
    currentPage, 
    totalPages,
    loadNextPage,
    loadPreviousPage
  } = useSearch();

  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q');
  const pageParam = searchParams.get('page');
  const sortParam = searchParams.get('sort');

  const [sortOption, setSortOption] = useState(sortParam || 'relevance');
  const [filteredResults, setFilteredResults] = useState([]);
  
  // Effect to perform search when the page loads with a query parameter
  useEffect(() => {
    if (queryParam) {
      const page = pageParam ? parseInt(pageParam) : 1;
      performSearch(queryParam, page);
    }
  }, [queryParam, pageParam, performSearch]);

  // Record this as a completed search for the admin "Searched Items" report.
  // Keyed on queryParam alone (not pageParam) so paging through results
  // doesn't recount the same search, and this only fires when a customer
  // actually lands on the results page - not on every live-typing keystroke.
  const trackedQueryRef = useRef(null);
  useEffect(() => {
    if (queryParam && trackedQueryRef.current !== queryParam) {
      trackedQueryRef.current = queryParam;
      trackProductSearch(queryParam);
    }
  }, [queryParam]);
  
  // Sort the results
  useEffect(() => {
    const results = [...searchResults];

    results.sort((a, b) => {
      switch(sortOption) {
        case 'newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'price-low':
          // Only applicable to products
          if (a.price && b.price) return a.price - b.price;
          if (a.price) return -1;
          if (b.price) return 1;
          return 0;
        case 'price-high':
          // Only applicable to products
          if (a.price && b.price) return b.price - a.price;
          if (a.price) return -1;
          if (b.price) return 1;
          return 0;
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'relevance':
        default:
          // Keep the original order (sorted by relevance from the API)
          return 0;
      }
    });
    
    setFilteredResults(results);
  }, [searchResults, sortOption]);

  const handleSortChange = (sort) => {
    setSortOption(sort);
    // Update URL params to include the sort option
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', sort);
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    setSearchParams(newParams);
    
    if (page > currentPage) {
      loadNextPage();
    } else if (page < currentPage) {
      loadPreviousPage();
    }
  };
  
  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-4">Search Results for "{searchQuery || queryParam}"</h1>

      {searchResults.length > 0 && filteredResults.length > 0 && (
        <div className="mb-2">
          <SearchSorting onSortChange={handleSortChange} />
        </div>
      )}
      
      {isSearching ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-pulse flex space-x-4 items-center">
            <div className="h-5 w-5 bg-primary-400 rounded-full"></div>
            <div className="h-5 w-5 bg-primary-400 rounded-full"></div>
            <div className="h-5 w-5 bg-primary-400 rounded-full"></div>
          </div>
          <p className="ml-4 text-lg text-gray-600">Searching...</p>
        </div>
      ) : searchResults.length === 0 ? (
        <div className="text-center py-12 px-4">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-[#1B365D]/10">
            <svg
              className="h-10 w-10 text-[#1B365D]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
              />
            </svg>
          </div>
          <p className="mt-5 text-xl font-semibold text-gray-800">No results found for "{searchQuery || queryParam}"</p>
          <p className="mt-2 text-gray-500">Try different keywords or check your spelling</p>

          <div className="mt-8 max-w-md mx-auto bg-[#1B365D]/5 border border-[#1B365D]/15 rounded-2xl p-6">
            <h3 className="font-semibold text-gray-800 mb-3">Search suggestions:</h3>
            <ul className="text-left text-gray-600 space-y-2">
              <li className="flex items-start">
                <svg className="mt-1 mr-2 w-4 h-4 text-[#1B365D] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Check for spelling mistakes</span>
              </li>
              <li className="flex items-start">
                <svg className="mt-1 mr-2 w-4 h-4 text-[#1B365D] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Try using more general terms</span>
              </li>
              <li className="flex items-start">
                <svg className="mt-1 mr-2 w-4 h-4 text-[#1B365D] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Try searching by product type</span>
              </li>
              <li className="flex items-start">
                <svg className="mt-1 mr-2 w-4 h-4 text-[#1B365D] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Reduce the number of search terms</span>
              </li>
            </ul>

            <div className="mt-6">
              <Link to="/products" className="inline-flex items-center justify-center w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#1B365D] hover:bg-[#152A4A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B365D] transition-colors">
                Browse All Products
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-gray-600">
              Found {totalResults} results for "{searchQuery || queryParam}"
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 xs:gap-4 md:gap-6">
            {filteredResults.map((result) => (
              <ProductCard key={result._id || result.id} product={result} />
            ))}
          </div>

          {/* Pagination */}
          {/* Related Searches */}
          {filteredResults.length > 0 && (
            <RelatedSearches query={searchQuery || queryParam} />
          )}
          
          {filteredResults.length > 0 && totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex items-center space-x-1">
                {/* Previous page button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className={`px-2 py-2 border rounded-l-md ${
                    currentPage <= 1 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-primary-50'
                  }`}
                  aria-label="Previous page"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                
                {/* Page numbers */}
                <div className="hidden sm:flex">
                  {/* Logic for displaying page numbers with ellipsis */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(pageNum => {
                      // Always show first and last page
                      if (pageNum === 1 || pageNum === totalPages) return true;
                      // Always show current page and pages adjacent to current
                      if (Math.abs(pageNum - currentPage) <= 1) return true;
                      // Don't show other pages
                      return false;
                    })
                    .map((pageNum, index, array) => {
                      // Check if we need to show ellipsis
                      const showEllipsisBefore = index > 0 && pageNum > array[index - 1] + 1;
                      const showEllipsisAfter = index < array.length - 1 && pageNum < array[index + 1] - 1;
                      
                      return (
                        <React.Fragment key={pageNum}>
                          {showEllipsisBefore && (
                            <span className="px-4 py-2 border bg-white text-gray-400">…</span>
                          )}
                          
                          <button
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-4 py-2 border ${
                              currentPage === pageNum
                                ? 'bg-primary-50 border-primary-500 text-primary-600'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                          
                          {showEllipsisAfter && (
                            <span className="px-4 py-2 border bg-white text-gray-400">…</span>
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>
                
                {/* Mobile pagination display */}
                <div className="sm:hidden px-4 py-2 border bg-white rounded-md">
                  Page {currentPage} of {totalPages}
                </div>
                
                {/* Next page button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className={`px-2 py-2 border rounded-r-md ${
                    currentPage >= totalPages 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-primary-50'
                  }`}
                  aria-label="Next page"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;
